
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HeroBlockConfiguration } from "@/types/block-configurations";
import {
  TypographyControls,
  AlignmentControls,
  PaddingControls,
  BackgroundControls,
  ResponsiveControls,
  ColorPicker,
} from "../shared";

interface HeroBlockPropertiesProps {
  config: HeroBlockConfiguration;
  onChange: (config: HeroBlockConfiguration) => void;
}

export function HeroBlockProperties({ config, onChange }: HeroBlockPropertiesProps) {
  const updateField = <K extends keyof HeroBlockConfiguration>(
    field: K,
    value: HeroBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div className="space-y-3">
      {/* Content */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Title</Label>
        <Input
          type="text"
          value={config.title ?? ''}
          onChange={(e) => updateField('title', e.target.value || undefined)}
          placeholder="Hero headline"
        />
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">Subtitle</Label>
        <Input
          type="text"
          value={config.subtitle ?? ''}
          onChange={(e) => updateField('subtitle', e.target.value || undefined)}
          placeholder="Supporting text"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium mb-1 block">Button text</Label>
          <Input
            type="text"
            value={config.ctaText ?? ''}
            onChange={(e) => updateField('ctaText', e.target.value || undefined)}
            placeholder="Get started"
          />
        </div>
        <div>
          <Label className="text-xs font-medium mb-1 block">Button link</Label>
          <Input
            type="url"
            value={config.ctaUrl ?? ''}
            onChange={(e) => updateField('ctaUrl', e.target.value || undefined)}
            placeholder="https://example.com"
          />
        </div>
      </div>

      {/* Layout Options — renders via mj-hero: fixed height when set, grows with content when empty */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium mb-1 block">Height (px)</Label>
          <Input
            type="number"
            value={config.minHeight ?? ''}
            onChange={(e) => updateField('minHeight', e.target.value ? parseInt(e.target.value) : undefined)}
            placeholder="Auto"
            min={100}
            max={1200}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Empty = size to content
          </p>
        </div>
        <div>
          <Label className="text-xs font-medium mb-1 block">Vertical Align</Label>
          <Select
            value={config.verticalAlign}
            onChange={(e) => updateField('verticalAlign', e.target.value as HeroBlockConfiguration['verticalAlign'])}
            disabled={config.minHeight === undefined}
          >
            <option value="top">Top</option>
            <option value="middle">Middle</option>
            <option value="bottom">Bottom</option>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Needs a fixed height
          </p>
        </div>
      </div>

      {/* Collapsible Sections */}
      <Accordion type="multiple" defaultValue={["background", "alignment"]} className="w-full">

        <AccordionItem value="alignment">
          <AccordionTrigger>Alignment</AccordionTrigger>
          <AccordionContent>
            <AlignmentControls
              value={config.alignment}
              onChange={(alignment) => updateField('alignment', alignment)}
              showVertical={false}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="background">
          <AccordionTrigger>Background</AccordionTrigger>
          <AccordionContent className="space-y-3">
            <BackgroundControls
              value={config.background}
              onChange={(background) => updateField('background', background)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="title-typography">
          <AccordionTrigger>Title Typography</AccordionTrigger>
          <AccordionContent>
            <TypographyControls
              value={config.titleTypography}
              onChange={(titleTypography) => updateField('titleTypography', titleTypography)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="subtitle-typography">
          <AccordionTrigger>Subtitle Typography</AccordionTrigger>
          <AccordionContent>
            <TypographyControls
              value={config.subtitleTypography}
              onChange={(subtitleTypography) => updateField('subtitleTypography', subtitleTypography)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="button">
          <AccordionTrigger>Button Style</AccordionTrigger>
          <AccordionContent className="space-y-3">
            <div>
              <Label className="text-xs font-medium mb-1 block">Background colour</Label>
              <ColorPicker
                value={config.ctaBackgroundColor ?? '#007bff'}
                onChange={(color) => updateField('ctaBackgroundColor', color)}
              />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">Text colour</Label>
              <ColorPicker
                value={config.ctaTextColor ?? '#ffffff'}
                onChange={(color) => updateField('ctaTextColor', color)}
              />
            </div>
            {/* Live preview */}
            {config.ctaText && (
              <div className="pt-1">
                <Label className="text-xs font-medium mb-1 block">Preview</Label>
                <div className="flex">
                  <span
                    className="inline-block rounded px-4 py-1.5 text-sm font-medium"
                    style={{
                      backgroundColor: config.ctaBackgroundColor || '#007bff',
                      color: config.ctaTextColor || '#ffffff',
                    }}
                  >
                    {config.ctaText}
                  </span>
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="border">
          <AccordionTrigger>Corner Radius</AccordionTrigger>
          <AccordionContent>
            {/* mj-hero supports border-radius only — no border width/style/color */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-medium">Radius</Label>
                <span className="text-xs text-muted-foreground">{config.border.radius}px</span>
              </div>
              <Slider
                value={[config.border.radius]}
                onValueChange={([radius]) => updateField('border', { ...config.border, radius })}
                min={0}
                max={50}
                step={1}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
